import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Filter } from 'lucide-react';
import { adminApi } from '@/services/api';
import { toast } from 'sonner';

const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [notifyEmails, setNotifyEmails] = useState('');
  const [savingNotify, setSavingNotify] = useState(false);

  const statusColors = {
    nueva: 'bg-blue-100 text-blue-800',
    contactada: 'bg-yellow-100 text-yellow-800',
    cotizada: 'bg-green-100 text-green-800',
    cerrada: 'bg-gray-100 text-gray-800'
  };

  const statusLabels = {
    nueva: 'Nueva',
    contactada: 'Contactada',
    cotizada: 'Cotizada',
    cerrada: 'Cerrada'
  };

  useEffect(() => {
    loadQuotes();
  }, [statusFilter]);

  useEffect(() => {
    loadNotifyConfig();
  }, []);

  const loadNotifyConfig = async () => {
    try {
      const res = await adminApi.getSiteConfig();
      setNotifyEmails(res.data?.quote_notify_emails || '');
    } catch (error) {
      // silencioso: si falla, el campo queda vacío
    }
  };

  const saveNotifyConfig = async () => {
    setSavingNotify(true);
    try {
      await adminApi.updateSiteConfig({ quote_notify_emails: notifyEmails.trim() });
      toast.success('Correos de notificación guardados');
    } catch (error) {
      toast.error('Error al guardar los correos');
    } finally {
      setSavingNotify(false);
    }
  };

  const loadQuotes = async () => {
    try {
      const params = statusFilter ? { estado: statusFilter } : {};
      const response = await adminApi.getQuotes(params);
      setQuotes(response.data);
    } catch (error) {
      toast.error('Error al cargar cotizaciones');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="font-heading text-2xl font-bold text-steel-dark uppercase tracking-wider">
          Cotizaciones
        </h1>
        
        <div className="flex items-center gap-2">
          <Filter size={20} className="text-steel-gray" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded focus:border-accent-primary focus:outline-none"
          >
            <option value="">Todos los estados</option>
            <option value="nueva">Nueva</option>
            <option value="contactada">Contactada</option>
            <option value="cotizada">Cotizada</option>
            <option value="cerrada">Cerrada</option>
          </select>
        </div>
      </div>

      {/* Config: correos de notificación de nuevas cotizaciones */}
      <div className="bg-white rounded shadow p-4 mb-6">
        <h2 className="font-heading font-bold text-sm text-steel-dark uppercase tracking-wider mb-1">
          Correos de notificación de nuevas cotizaciones
        </h2>
        <p className="text-xs text-steel-gray mb-3">
          Cada cotización nueva se enviará también a estos correos. Sepáralos con comas.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={notifyEmails}
            onChange={(e) => setNotifyEmails(e.target.value)}
            placeholder="correo1@dominio.com, correo2@dominio.com"
            className="flex-1 px-3 py-2 border border-gray-200 rounded text-sm focus:border-accent-primary focus:outline-none"
          />
          <button
            onClick={saveNotifyConfig}
            disabled={savingNotify}
            className="btn-primary px-4 py-2 text-sm whitespace-nowrap disabled:opacity-50"
          >
            {savingNotify ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Folio</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Cliente</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Empresa</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Productos</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Fecha</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={7} className="px-4 py-4">
                      <div className="animate-pulse h-4 bg-gray-200 rounded" />
                    </td>
                  </tr>
                ))
              ) : quotes.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-steel-gray">
                    No hay cotizaciones {statusFilter && `con estado "${statusLabels[statusFilter]}"`}
                  </td>
                </tr>
              ) : (
                quotes.map((quote) => (
                  <tr key={quote.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-sm font-medium text-steel-dark">
                      {quote.folio}
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-steel-dark">{quote.cliente_nombre}</p>
                        <p className="text-xs text-steel-gray">{quote.cliente_email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-steel-gray">{quote.cliente_empresa || '-'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="bg-steel-dark text-white text-xs px-2 py-1 rounded">
                        {quote.productos_count}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded ${statusColors[quote.estado] || statusColors.nueva}`}>
                        {statusLabels[quote.estado] || quote.estado}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-steel-gray">
                      {new Date(quote.created_at).toLocaleDateString('es-MX')}
                    </td>
                    <td className="px-4 py-3">
                      <Link 
                        to={`/admin/cotizaciones/${quote.id}`}
                        className="p-2 text-accent-primary hover:bg-accent-primary/10 rounded inline-flex"
                      >
                        <Eye size={18} />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminQuotes;
